import { Link } from "react-router-dom";
import { Flame, Snowflake, Wrench, ShieldCheck, Timer, ArrowRight } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { IMAGES } from "../constants";

const ServicePanel = ({ icon: Icon, title, accent, items, image, alt, ctaTestId }) => (
  <FadeUp className="group relative rounded-3xl overflow-hidden border border-white/10 bg-[#12151C]">
    <div className="relative h-64 md:h-80 overflow-hidden">
      <img
        src={image}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#12151C] via-transparent to-transparent" />
    </div>
    <div className="p-8 md:p-10">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6"
        style={{ background: `${accent}1A`, color: accent }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight mb-4">
        {title}
      </h3>
      <ul className="space-y-3 mb-8">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-[#94A3B8] text-sm md:text-base">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
            {item}
          </li>
        ))}
      </ul>
      <Link
        to="/contatti"
        data-testid={ctaTestId}
        className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] transition-colors duration-300"
        style={{ color: accent }}
      >
        Richiedi intervento
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  </FadeUp>
);

export const Services = () => (
  <section data-testid="services-section" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <FadeUp>
        <Eyebrow>I nostri servizi</Eyebrow>
        <h1 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl">
          Due specialità.
          <span className="text-[#94A3B8]"> Zero improvvisazione.</span>
        </h1>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
        <ServicePanel
          icon={Flame}
          title="Caldaie"
          accent="#FF6B00"
          image={IMAGES.caldaia}
          alt="Caldaia a gas in acciaio"
          ctaTestId="service-caldaie-cta"
          items={[
            "Pronto intervento guasti e blocchi caldaia",
            "Manutenzione ordinaria e bollino",
            "Sostituzione e installazione a condensazione",
            "Prova fumi e controllo efficienza",
          ]}
        />
        <ServicePanel
          icon={Snowflake}
          title="Condizionatori"
          accent="#00D2FF"
          image={IMAGES.condizionatore}
          alt="Unità interna di condizionatore"
          ctaTestId="service-condizionatori-cta"
          items={[
            "Installazione climatizzatori mono e multi split",
            "Ricarica gas e ricerca perdite",
            "Pulizia filtri e sanificazione",
            "Riparazione schede elettroniche e motori",
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
        {[
          { model: "Beretta Ciao X", price: "1.050€", testId: "offer-beretta" },
          { model: "Ariston Cares S", price: "1.190€", testId: "offer-ariston" },
        ].map((o, i) => (
          <FadeUp key={o.model} delay={i * 0.1}>
            <div
              data-testid={o.testId}
              className="rounded-3xl border border-[#FF6B00]/30 bg-gradient-to-br from-[#FF6B00]/[0.08] to-transparent p-8 md:p-10 h-full flex flex-col transition-all duration-300 hover:border-[#FF6B00]/60 hover:shadow-[0_0_40px_rgba(255,107,0,0.12)]"
            >
              <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#FF6B00] mb-4">
                Offerta installazione
              </p>
              <h4 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">
                Caldaia {o.model}
              </h4>
              <p className="text-[#94A3B8] text-sm mt-1">a condensazione</p>
              <p className="font-display font-semibold text-5xl tracking-tighter mt-6 mb-6">
                <span className="text-lg text-[#94A3B8] font-normal align-top">da</span> {o.price}
              </p>
              <ul className="space-y-2.5 text-sm text-[#94A3B8] mb-8 flex-1">
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF6B00] shrink-0" />Smaltimento della vecchia caldaia incluso</li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF6B00] shrink-0" />Certificazioni e pratiche incluse</li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF6B00] shrink-0" />Zero costi aggiuntivi</li>
              </ul>
              <Link
                to={`/contatti?offerta=${encodeURIComponent(`Installazione caldaia ${o.model} a condensazione a partire da ${o.price} — smaltimento e certificazioni inclusi, zero costi aggiuntivi`)}`}
                data-testid={`${o.testId}-cta`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B00] text-[#07090D] font-semibold px-8 py-3.5 text-sm uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,0,0.4)] active:scale-[0.98] w-fit"
              >
                Richiedi questa offerta
              </Link>
            </div>
          </FadeUp>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-8">
        {[
          { icon: Timer, title: "Intervento rapido", text: "In giornata per le urgenze, entro 24-48h per gli interventi programmati." },
          { icon: ShieldCheck, title: "Lavoro garantito", text: "Ricambi originali e garanzia scritta su ogni riparazione eseguita." },
          { icon: Wrench, title: "Tecnici certificati", text: "Abilitazione F-Gas e aggiornamento continuo su tutte le marche." },
        ].map((f, i) => (
          <FadeUp key={f.title} delay={i * 0.1}>
            <div
              data-testid={`feature-${i}`}
              className="rounded-3xl border border-white/10 p-8 h-full transition-all duration-300 hover:border-white/25 hover:bg-white/[0.02]"
            >
              <f.icon className="w-6 h-6 text-[#FF6B00] mb-5" />
              <h4 className="font-display font-medium text-lg mb-2">{f.title}</h4>
              <p className="text-sm text-[#94A3B8] leading-relaxed">{f.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);
